import * as React from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import { useContext, useEffect, useState } from 'react';
import { AlumnoType, TorneoType, categoriasFemenina, categoriasMasculina, categoriasMasculinaFederado, nivelesFemenina, nivelesFemeninaFederado, nivelesMasculina, nivelesMasculinaFederado } from '../../types/types';
import { Accordion, AccordionDetails, AccordionSummary, Checkbox, Chip, Container, DialogTitle, Divider, FormControlLabel, FormGroup, Grid, Paper, Switch, Typography } from '@mui/material';
import { UserContext } from '../../contexts/userContext';
import { grey } from '@mui/material/colors';
import { IndicatorButton } from '../IndicatorButton';
import { useAddOrUpdateTorneo } from '../../hooks/useTorneos';
import { useGetAlumnos } from '../../hooks/getAlumnos';
import dayjs from 'dayjs';
import { ExpandMore } from '@mui/icons-material';
import hash from 'object-hash';

export interface WizardTorneoProps {
  torneo?: TorneoType;
  open: boolean;
  setOpen: (open:boolean) => void;
  onClose: () => void;
  refetchLista: () => void;
}

const dateFormat = 'DD/MM/YYYY';

export default function WizardTorneo(props:WizardTorneoProps) {
  const { torneo, open, onClose, setOpen, refetchLista } = props;
  const [ mutableTorneo, setMutableTorneo ] = useState<TorneoType | undefined>();
  const { user } = useContext(UserContext);

  const torneoApiCall = useAddOrUpdateTorneo({ torneo: mutableTorneo }, user);
  const alumnos = useGetAlumnos(user, false);

  useEffect(() => {
    if (!torneoApiCall.isLoading) setTimeout(() => refetchLista(), 500);
  }, [torneoApiCall.isLoading]);

  useEffect(() =>{
    setMutableTorneo(torneo);
  }, [torneo]);

  let titulo = '';
  if (torneo) titulo = 'Editar torneo';
  if (!torneo) titulo = 'Crear Nuevo torneo';

  const descartar = () => {
    setMutableTorneo(undefined);
    setOpen(false);
  };

  const handleSubmit = () => {
    torneoApiCall.refetch();
    onClose();
  };

  const handleCheckboxChange = (fieldName:string, itemName:string, checked:boolean) => {
    let items =  mutableTorneo?.[fieldName as keyof TorneoType] as string[] ?? [];
    if (checked && !items.includes(itemName)) items.push(itemName);
    if (!checked) items = items.filter((i) => i !== itemName);
    setMutableTorneo((prev) => ({
      ...prev,
      [fieldName]: items,
    }));
  };

  // categoriasFemenina
  // categoriasMasculina
  // categoriasMasculinaFederado
  const categoriasDisponibles:string[] = [];
  if (mutableTorneo?.generos?.includes('F')) categoriasDisponibles.push(...categoriasFemenina);
  if (mutableTorneo?.generos?.includes('M') && mutableTorneo.tipo === 'federado') categoriasDisponibles.push(...categoriasMasculinaFederado);
  if (mutableTorneo?.generos?.includes('M') && mutableTorneo.tipo === 'no-federado') categoriasDisponibles.push(...categoriasMasculina);
  if (mutableTorneo?.generos?.includes('M') && mutableTorneo.tipo === 'abierto') categoriasDisponibles.push(...categoriasMasculina, ...categoriasMasculinaFederado);

  // Eliminar repetidos
  let categoriasIndex = 0;
  const categoriasSeen = new Set<string>();
  for (const item of categoriasDisponibles) {
    if (!categoriasSeen.has(item)) {
      categoriasDisponibles[categoriasIndex++] = item;
      categoriasSeen.add(item);
    }
  }
  categoriasDisponibles.length = categoriasIndex;

  // nivelesFemenina
  // nivelesFemeninaFederado
  // nivelesMasculina
  // nivelesMasculinaFederado
  const nivelesDisponibles:string[] = [];
  if (mutableTorneo?.generos?.includes('F') && mutableTorneo.tipo === 'no-federado') nivelesDisponibles.push(...nivelesFemenina);
  if (mutableTorneo?.generos?.includes('F') && mutableTorneo.tipo === 'federado') nivelesDisponibles.push(...nivelesFemeninaFederado);
  if (mutableTorneo?.generos?.includes('M') && mutableTorneo.tipo === 'federado') nivelesDisponibles.push(...nivelesMasculinaFederado);
  if (mutableTorneo?.generos?.includes('M') && mutableTorneo.tipo === 'no-federado') nivelesDisponibles.push(...nivelesMasculina);
  if (mutableTorneo?.generos?.includes('M') && mutableTorneo.tipo === 'abierto') nivelesDisponibles.push(...nivelesMasculina, ...nivelesMasculinaFederado);
  if (mutableTorneo?.generos?.includes('F') && mutableTorneo.tipo === 'abierto') nivelesDisponibles.push(...nivelesFemenina, ...nivelesFemeninaFederado);

  // Eliminar repetidos
  let nivelesIndex = 0;
  const nivelesSeen = new Set<string>();
  for (const item of nivelesDisponibles) {
    if (!nivelesSeen.has(item)) {
      nivelesDisponibles[nivelesIndex++] = item;
      nivelesSeen.add(item);
    }
  }
  nivelesDisponibles.length = nivelesIndex;

  const generos = ['M', 'F'];
  const tipos = ['federado', 'no-federado', 'abierto'];

  useEffect(() => {
    const alumnosFiltrados = alumnos.data?.filter(alumno => {
      if (!mutableTorneo?.niveles?.includes(alumno.nivel ?? '**')) return false;
      if (!mutableTorneo?.categorias?.includes(alumno.categoria ?? '**')) return false;
      if (!mutableTorneo?.generos?.includes(alumno.genero ?? '**')) return false;
      if (mutableTorneo?.tipo === 'federado' && !alumno.federado) return false;
      if (mutableTorneo?.tipo === 'no-federado' && alumno.federado) return false;
      return true;
    }).map((a) => a._id);
    setMutableTorneo((prev) => ({ ...prev, participantes: alumnosFiltrados }));
  }, [hash(mutableTorneo ?? {})]);

  return (
    <div>
      <Dialog open={open} onClose={()=>{}} closeAfterTransition >
        <DialogTitle>{titulo}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                margin="dense"
                id="nombre-torneo"
                label="Nombre"
                type="text"
                fullWidth
                variant="standard"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setMutableTorneo((prev) => ({ ...prev, nombre: (event.target as HTMLInputElement).value as string }));
                }}
                value={mutableTorneo?.nombre ?? ''}
                required
                error={!mutableTorneo?.nombre}
                helperText={!mutableTorneo?.nombre ? 'El Nombre no puede quedar vacio' : ''}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                margin="dense"
                id="lugar-torneo"
                label="Lugar"
                type="text"
                fullWidth
                variant="standard"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setMutableTorneo((prev) => ({ ...prev, lugar: (event.target as HTMLInputElement).value as string }));
                }}
                value={mutableTorneo?.lugar ?? ''}
                required
                error={!mutableTorneo?.lugar}
                helperText="El lugar no puede quedar vacio"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                margin="dense"
                id="fecha-torneo"
                label="Fecha"
                type="text"
                fullWidth
                variant="standard"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setMutableTorneo((prev) => ({ ...prev, fecha: (event.target as HTMLInputElement).value }));
                }}
                value={mutableTorneo?.fecha || ''}
                error={!dayjs(mutableTorneo?.fecha, dateFormat, true).isValid()}
                helperText={!dayjs(mutableTorneo?.fecha, dateFormat, true).isValid() ? 'Formato de fecha invalido' : ''}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                margin="dense"
                id="torneo-inscripcion"
                label="Valor Inscripcion"
                type="text"
                fullWidth
                variant="standard"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setMutableTorneo((prev) => ({ ...prev, inscripcion: Number((event.target as HTMLInputElement).value) }));
                }}
                value={mutableTorneo?.inscripcion ?? ''}
                required
                error={!mutableTorneo?.inscripcion}
                helperText="El valor de la inscripcion no puede quedar vacio"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                margin="dense"
                id="torneo-organizador"
                label="Organizador"
                type="text"
                fullWidth
                variant="standard"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setMutableTorneo((prev) => ({ ...prev, organizador: (event.target as HTMLInputElement).value }));
                }}
                value={mutableTorneo?.organizador ?? ''}
                required
                error={!mutableTorneo?.organizador}
                helperText="El organizador no puede quedar vacio"
              />
            </Grid>
            <Grid item xs={12}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography>Tipo de torneo</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <FormGroup>
                    {
                      tipos.map((tipo) => (
                        <FormControlLabel
                          control={
                            <Checkbox
                              key={`checkbox-tipo-${tipo}`}
                              onChange={ (event: React.ChangeEvent<HTMLInputElement>) => {
                                if (mutableTorneo?.tipo !== tipo && event.target.checked){
                                  setMutableTorneo((prev) => ({
                                    ...prev,
                                    tipo,
                                    niveles: [],
                                    categorias: [],
                                  }));
                                }
                              }}
                              checked={mutableTorneo?.tipo === tipo}
                            />
                          }
                          label={tipo} />
                      ))
                    }
                  </FormGroup>
                </AccordionDetails>
              </Accordion>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography>Generos</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <FormGroup>
                    {
                      generos.map((genero) => (
                        <FormControlLabel
                          control={
                            <Checkbox
                              key={`checkbox-generos-${genero}`}
                              onChange={(event) => {
                                setMutableTorneo((prev) => ({
                                  ...prev,
                                  niveles: [],
                                  categorias: [],
                                }));
                                handleCheckboxChange('generos', genero, event.target.checked);
                              }}
                              checked={mutableTorneo?.generos?.includes(genero)}
                            />
                          }
                          label={genero === 'F' ? 'Femenina' : 'Masculina'} />
                      ))
                    }
                  </FormGroup>
                </AccordionDetails>
              </Accordion>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography>Categorias</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <FormGroup>
                    {
                      categoriasDisponibles.map((categoria) => (
                        <FormControlLabel
                          control={
                            <Checkbox
                              key={`checkbox-categoria-${categoria}`}
                              onChange={(event) => handleCheckboxChange('categorias', categoria, event.target.checked)}
                              checked={mutableTorneo?.categorias?.includes(categoria)}
                            />
                          }
                          label={categoria} />
                      ))
                    }
                  </FormGroup>
                </AccordionDetails>
              </Accordion>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography>Niveles</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <FormGroup>
                    {
                      nivelesDisponibles.map((nivel) => (
                        <FormControlLabel
                          control={
                            <Checkbox
                              key={`checkbox-niveles-${nivel}`}
                              onChange={(event) =>handleCheckboxChange('niveles', nivel, event.target.checked)}
                              name={`niveles-${nivel}`}
                              checked={mutableTorneo?.niveles?.includes(nivel)}
                            />
                          }
                          label={nivel} />
                      ))
                    }
                  </FormGroup>
                </AccordionDetails>
              </Accordion>
            </Grid>
            <Grid item xs={12}>
              <Paper elevation={3} style={{ padding: 8, marginTop: 16 }}>
                <Typography style={{ color: grey[800], fontSize: 14 }}>Participantes</Typography>
                <Divider />
                <Container   style={{ columns:'3 auto', width: 600 }} >
                  {alumnos?.data?.filter((a) => mutableTorneo?.participantes?.includes(a._id)).map((a:AlumnoType) => (
                    <Chip
                      key={a._id}
                      label={`${a.name} ${a.apellido}`}
                      onDelete={() => {
                        const participantes = mutableTorneo?.participantes?.filter((p) => p !== a._id);
                        setMutableTorneo((prev) => ({ ...prev, participantes }));
                      }}
                      size="small"
                      style={{ margin: 4 }}
                    />
                  ))}
                </Container>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            disabled={torneoApiCall.isLoading}
            onClick={() =>{
              if (torneo) setMutableTorneo(torneo);
              else descartar();
              onClose();
            }}
          >
        Descartar
          </Button>
          <IndicatorButton
            label='Guardar Cambios'
            disabled={
              !mutableTorneo?.nombre
            || !mutableTorneo.fecha
            || !mutableTorneo.lugar
            || !mutableTorneo.organizador
            || !mutableTorneo.inscripcion
            }
            loading={torneoApiCall.isLoading}
            onClick={handleSubmit}
          />
        </DialogActions>
      </Dialog>
    </div>
  );
}
